import { ExportedPreset, AnimationCategory, ButtonSubType } from '../types';

// ===== Backgrounds (Canvas-based particle animations) =====
export const generateBackgroundJS = (preset: ExportedPreset): string => {
  const config = preset.config;
  const colors = Array.isArray(config.color) ? config.color : [config.color];

  return `// ${preset.name} - Background Animation
// Category: ${preset.category}
(function() {
  'use strict';

  class ${preset.name.replace(/\s/g, '')} {
    constructor(selector) {
      this.canvas = typeof selector === 'string' 
        ? document.querySelector(selector) 
        : selector;
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d');
      this.config = ${JSON.stringify(config, null, 2)};
      this.colors = ${JSON.stringify(colors)};
      this.particles = [];
      this.running = false;
      this.animationId = null;
      this.resize();
      window.addEventListener('resize', () => this.resize());
    }

    resize() {
      this.canvas.width = this.canvas.offsetWidth || 800;
      this.canvas.height = this.canvas.offsetHeight || 600;
    }

    init() {
      this.particles = [];
      const count = this.config.count || 100;
      for (let i = 0; i < count; i++) {
        this.particles.push({
          x: Math.random() * this.canvas.width,
          y: Math.random() * this.canvas.height,
          vx: (Math.random() - 0.5) * this.config.speed,
          vy: (Math.random() - 0.5) * this.config.speed * 2,
          size: this.config.size || 3,
          color: this.colors[Math.floor(Math.random() * this.colors.length)]
        });
      }
      return this;
    }

    start() {
      if (this.running) return this;
      this.running = true;
      this.init();
      this.animate();
      return this;
    }

    stop() {
      this.running = false;
      if (this.animationId) cancelAnimationFrame(this.animationId);
      return this;
    }

    destroy() {
      this.stop();
      window.removeEventListener('resize', () => this.resize());
    }

    animate() {
      if (!this.running) return;
      const c = this.config;
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.globalAlpha = c.opacity;

      this.particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        
        if (c.direction === 'down') p.y += c.speed;
        if (c.direction === 'up') p.y -= c.speed;
        if (c.direction === 'right') p.x += c.speed;
        if (c.direction === 'left') p.x -= c.speed;

        if (p.x < 0) p.x = this.canvas.width;
        if (p.x > this.canvas.width) p.x = 0;
        if (p.y < 0) p.y = this.canvas.height;
        if (p.y > this.canvas.height) p.y = 0;

        this.ctx.fillStyle = p.color;
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        this.ctx.fill();
      });

      this.animationId = requestAnimationFrame(() => this.animate());
    }
  }

  window.${preset.name.replace(/\s/g, '')} = ${preset.name.replace(/\s/g, '')};
})();`;
};

// ===== Buttons (Border Glow - CSS animation) =====
export const generateBorderGlowJS = (preset: ExportedPreset): string => {
  const config = preset.config;
  const colors = Array.isArray(config.color) ? config.color : [config.color];
  const glowColor = colors[0] || '#ff0000';
  const borderRadius = config.borderRadius || 10;
  const speed = config.speed || 1;

  const duration = 20 / speed;

  return `// ${preset.name} - Border Glow Button Animation
// Category: ${preset.category}
(function() {
  'use strict';

  const GLOW_COLORS = ${JSON.stringify(colors)};
  const BORDER_RADIUS = ${borderRadius};
  const DURATION = ${duration};
  const BLUR = ${config.intensity * 8};

  function applyBorderGlow(element, colors) {
    element.style.cssText += \`
      position: relative;
      z-index: 0;
      border-radius: \${BORDER_RADIUS}px;
      background: #111;
      color: #fff;
      cursor: pointer;
      border: none;
      outline: none;
      overflow: hidden;
    \`;

    const style = document.createElement('style');
    style.textContent = \`
      .glow-btn-\${element.dataset.animate} {
        position: relative;
        z-index: 0;
        border-radius: \${BORDER_RADIUS}px;
        background: #111;
        color: #fff;
        cursor: pointer;
        border: none;
        outline: none;
        overflow: hidden;
      }
      .glow-btn-\${element.dataset.animate}:before {
        content: '';
        background: linear-gradient(45deg, \${colors.join(', ')});
        position: absolute;
        top: -2px;
        left: -2px;
        background-size: 400%;
        z-index: -1;
        filter: blur(\${BLUR}px);
        width: calc(100% + 4px);
        height: calc(100% + 4px);
        animation: glowing-\${element.dataset.animate} \${DURATION}s linear infinite;
        opacity: 1;
        border-radius: \${BORDER_RADIUS}px;
      }
      .glow-btn-\${element.dataset.animate}:after {
        z-index: -1;
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        background: #111;
        left: 0;
        top: 0;
        border-radius: \${BORDER_RADIUS}px;
      }
      .glow-btn-\${element.dataset.animate}:active {
        color: #000;
      }
      .glow-btn-\${element.dataset.animate}:active:after {
        background: transparent;
      }
      @keyframes glowing-\${element.dataset.animate} {
        0% { background-position: 0 0; }
        50% { background-position: 400% 0; }
        100% { background-position: 0 0; }
      }
    \`;
    document.head.appendChild(style);
    element.classList.add('glow-btn-' + element.dataset.animate);
  }

  document.querySelectorAll('[data-animate="${preset.name.replace(/\s/g, '')}"]').forEach(el => {
    applyBorderGlow(el, GLOW_COLORS);
  });

  window.apply${preset.name.replace(/\s/g, '')} = applyBorderGlow;
})();`;
};

// ===== Buttons (Firefly animation - canvas overlay) =====
export const generateFireflyJS = (preset: ExportedPreset): string => {
  const config = preset.config;

  return `// ${preset.name} - Firefly Button Animation
// Category: ${preset.category}
(function() {
  'use strict';

  function applyFirefly(element, config) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const fireflySize = config.fireflySize || 6;
    const fireflyGlow = config.fireflyGlow || 15;
    const fireflySpeed = config.fireflySpeed || 1;
    const fireflyColor = config.fireflyColor || '#00ffaa';

    canvas.style.cssText = \`
      position: absolute;
      top: 0;
      left: 0;
      pointer-events: none;
      z-index: 0;
    \`;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position: relative; display: inline-block; overflow: visible;';
    wrapper.appendChild(canvas);
    wrapper.appendChild(element);
    element.parentNode.replaceChild(wrapper, element);

    const updateCanvas = () => {
      canvas.width = element.offsetWidth;
      canvas.height = element.offsetHeight;
    };

    updateCanvas();
    const resizeObserver = new ResizeObserver(updateCanvas);
    resizeObserver.observe(element);

    let animationId;

    const animate = () => {
      const btnWidth = element.offsetWidth;
      const btnHeight = element.offsetHeight;
      if (btnWidth <= 0 || btnHeight <= 0) {
        animationId = requestAnimationFrame(animate);
        return;
      }

      const time = Date.now() * 0.001 * fireflySpeed;
      const speed = 30;
      const perimeter = (btnWidth + btnHeight) * 2;
      const position = (time * speed) % perimeter;

      let dotX = 0;
      let dotY = 0;

      if (position < btnWidth) {
        dotX = position;
        dotY = 0;
      } else if (position < btnWidth + btnHeight) {
        dotX = btnWidth;
        dotY = position - btnWidth;
      } else if (position < btnWidth * 2 + btnHeight) {
        dotX = btnWidth - (position - btnWidth - btnHeight);
        dotY = btnHeight;
      } else {
        dotX = 0;
        dotY = btnHeight - (position - btnWidth * 2 - btnHeight);
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const blinkAlpha = 0.7 + Math.sin(time * 10) * 0.3;

      ctx.globalAlpha = blinkAlpha * 0.5;
      const gradient = ctx.createRadialGradient(dotX, dotY, 0, dotX, dotY, fireflyGlow * 2);
      gradient.addColorStop(0, fireflyColor);
      gradient.addColorStop(0.3, fireflyColor + '80');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(dotX, dotY, fireflyGlow * 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalAlpha = blinkAlpha;
      ctx.fillStyle = fireflyColor;
      ctx.beginPath();
      ctx.arc(dotX, dotY, fireflySize, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalAlpha = blinkAlpha * 0.9;
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(dotX, dotY, fireflySize * 0.4, 0, Math.PI * 2);
      ctx.fill();

      animationId = requestAnimationFrame(animate);
    };

    animate();
  }

  document.querySelectorAll('[data-animate="${preset.name.replace(/\s/g, '')}"]').forEach(el => {
    applyFirefly(el, ${JSON.stringify(config, null, 2)});
  });

  window.apply${preset.name.replace(/\s/g, '')} = applyFirefly;
})();`;
};

// ===== Text Animations =====
export const generateTextJS = (preset: ExportedPreset): string => {
  const config = preset.config;
  const color = Array.isArray(config.color) ? config.color[0] : config.color;

  return `// ${preset.name} - Text Animation
// Category: ${preset.category}
(function() {
  'use strict';

  function applyTextAnimation(element, config) {
    const text = element.textContent;
    const speed = config.speed || 1;
    const style = config.subType || 'fade';
    
    element.style.cssText = \`
      color: ${color};
      font-size: \${(config.size || 3) * 10}px;
    \`;

    switch (style) {
      case 'typewriter':
      case 'typing':
        element.textContent = '';
        let i = 0;
        const interval = setInterval(() => {
          if (i >= text.length) {
            clearInterval(interval);
            return;
          }
          element.textContent += text[i];
          i++;
        }, 100 / speed);
        break;
        
      case 'fade':
        element.style.transition = 'opacity ' + (1 / speed) + 's';
        let fadeDir = true;
        setInterval(() => {
          element.style.opacity = fadeDir ? 1 : 0;
          fadeDir = !fadeDir;
        }, 1000 / speed);
        break;
        
      case 'wave':
        // Add CSS animation
        const styleEl = document.createElement('style');
        styleEl.textContent = \`
          @keyframes wave-\${Math.random().toString(36).substr(2, 9)} {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
        \`;
        document.head.appendChild(styleEl);
        element.style.animation = \`wave-\${styleEl.textContent.match(/wave-(\\w+)/)?.[1]} \${2 / speed}s ease-in-out infinite\`;
        break;
        
      case 'gradient':
        element.style.cssText = \`
          background: linear-gradient(90deg, ${color}, #fff, ${color});
          background-size: 200% 100%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradient-move \${3 / speed}s linear infinite;
        \`;
        break;
    }
  }

  document.querySelectorAll('[data-animate="${preset.name.replace(/\s/g, '')}"]').forEach(el => {
    applyTextAnimation(el, ${JSON.stringify(config, null, 2)});
  });

  window.apply${preset.name.replace(/\s/g, '')} = applyTextAnimation;
})();`;
};

// ===== Loaders =====
export const generateLoaderJS = (preset: ExportedPreset): string => {
  const config = preset.config;
  const color = Array.isArray(config.color) ? config.color[0] : config.color;

  return `// ${preset.name} - Loader Animation
// Category: ${preset.category}
(function() {
  'use strict';

  function createLoader(container, config) {
    const size = config.size || 40;
    const speed = config.speed || 1;
    const style = '${preset.subType}';
    const color = '${color}';
    
    const loader = document.createElement('div');
    loader.style.cssText = \`
      width: \${size}px;
      height: \${size}px;
      border: 3px solid \${color}22;
      border-top-color: \${color};
      border-radius: 50%;
      animation: spin \${1 / speed}s linear infinite;
    \`;
    
    const styleEl = document.createElement('style');
    styleEl.textContent = \`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    \`;
    document.head.appendChild(styleEl);
    
    if (typeof container === 'string') {
      document.querySelector(container)?.appendChild(loader);
    } else {
      container.appendChild(loader);
    }
    
    return {
      stop: () => loader.remove(),
      element: loader
    };
  }

  document.querySelectorAll('[data-loader="${preset.name.replace(/\s/g, '')}"]').forEach(el => {
    createLoader(el, ${JSON.stringify(config, null, 2)});
  });

  window.create${preset.name.replace(/\s/g, '')} = createLoader;
})();`;
};

// ===== Unified Export Generator =====
export const generateExportCode = (
  preset: ExportedPreset,
  format: 'js' | 'json' | 'html'
): string => {
  if (format === 'json') {
    return JSON.stringify(preset, null, 2);
  }

  if (format === 'html') {
    return generateHTMLTemplate(preset);
  }

  // JS format - choose based on category
  switch (preset.category) {
    case 'backgrounds':
      return generateBackgroundJS(preset);
    case 'buttons':
      if (preset.subType === 'border-glow') {
        return generateBorderGlowJS(preset);
      }
      return generateFireflyJS(preset);
    case 'text':
      return generateTextJS(preset);
    case 'loaders':
      return generateLoaderJS(preset);
    default:
      return generateBackgroundJS(preset);
  }
};

// ===== HTML Template =====
const generateHTMLTemplate = (preset: ExportedPreset): string => {
  const code = generateExportCode(preset, 'js');

  return `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${preset.name} - Animation Demo</title>
  <style>
    body {
      margin: 0;
      padding: 40px;
      background: #1a1a1a;
      min-height: 100vh;
    }
    .demo-container {
      max-width: 800px;
      margin: 0 auto;
    }
  </style>
</head>
<body>
  <div class="demo-container">
    <h1 style="color: #fff; font-family: sans-serif;">${preset.name}</h1>
    <p style="color: #888;">Category: ${preset.category} | Type: ${preset.subType}</p>
    
    <!-- Add your element here with data-animate attribute -->
    <${preset.category === 'buttons' ? 'button' : 'div'} 
      data-animate="${preset.name.replace(/\s/g, '')}"
      style="
        ${preset.category === 'buttons' ? 'padding: 16px 32px; background: #2a2a2a; color: #fff; border: 2px solid #444; border-radius: 8px;' : ''}
      ">
      ${preset.category === 'buttons' ? 'Click Me' : 'Animation Element'}
    </${preset.category === 'buttons' ? 'button' : 'div'}>
  </div>

  <script>
${code}
  </script>
</body>
</html>`;
};

export const getFileExtension = (format: 'js' | 'json' | 'html'): string => {
  switch (format) {
    case 'json':
      return 'json';
    case 'html':
      return 'html';
    default:
      return 'js';
  }
};

export const getMimeType = (format: 'js' | 'json' | 'html'): string => {
  switch (format) {
    case 'json':
      return 'application/json';
    case 'html':
      return 'text/html';
    default:
      return 'application/javascript';
  }
};
